/**
 * Per-token live activity sidebar for the community page.
 *
 * Seeds from /api/stats/recent-activity?mint=<mint> on mount, then
 * subscribes to slurpWs and filters every event to this token's mint.
 * No new backend infrastructure — reuses the existing firehose, client
 * filters. See feedback_no_rpc_polling: no setInterval polling, only
 * mount-time fetch + WS push.
 */
import React, { useEffect, useMemo, useState } from "react";
import { slurpApi, slurpWs, profileCache } from "../../lib/slurpApi";

const API_BASE = process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";
const MAX_ENTRIES = 40;
const MIN_USD = 0.1;

const fmtUsd = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(2)}`;
  return `<$0.01`;
};
const fmtWhole = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(0);
};
// Wallet display: last 4 chars only (no ellipsis prefix). Tight enough
// for the horizontal chip strip without losing identity uniqueness in
// a typical 50-event window.
const shortAddr = (w) => (w ? w.slice(-4) : "");
const fmtAgo = (ts) => {
  const ms = Date.now() - ts;
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

// Filter set adapts to the token's fee mode. Burns/LP only appear on
// the tokens that actually generate those events — no point offering
// a "Burns" pill on a default-mode token that will never have any.
function buildFilters(feeMode) {
  const base = [["all", "All"], ["buy", "Buys"], ["sell", "Sells"]];
  if (feeMode === "burn") base.push(["burn", "Burns"]);
  if (feeMode === "lp_compound") base.push(["compound", "LP"]);
  return base;
}

export default function TokenLiveFeed({ mint, feeMode, symbol }) {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  // Wallet → profile snippet. Seeded from the shared profileCache and
  // backfilled on demand for any wallet we don't have yet. Null = we
  // looked it up and the wallet has no profile.
  const [profiles, setProfiles] = useState({});
  // Re-render every 30s so the "Xs ago" labels tick up. Doesn't poll
  // the network — just bumps state.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Backfill profiles for any wallet we don't already have. Promise
  // chain (not Promise.all) so a wave of new events doesn't fire 50
  // concurrent requests — slurpApi.getProfile is memoised via
  // profileCache so a wallet we've already fetched is free.
  useEffect(() => {
    const wallets = new Set();
    for (const e of entries) {
      if (!e.wallet) continue;
      if (profiles[e.wallet] !== undefined) continue;
      // profileCache hit (from any prior page) — read directly
      const cached = profileCache.get(e.wallet);
      if (cached !== undefined) {
        setProfiles((p) => ({ ...p, [e.wallet]: cached }));
        continue;
      }
      wallets.add(e.wallet);
    }
    if (wallets.size === 0) return;
    let cancelled = false;
    (async () => {
      for (const w of wallets) {
        if (cancelled) return;
        try {
          const r = await slurpApi.getProfile(w);
          if (cancelled) return;
          setProfiles((p) => ({ ...p, [w]: r?.profile ?? null }));
        } catch {
          // 404 = no profile; mark null so we don't refetch
          if (!cancelled) setProfiles((p) => ({ ...p, [w]: null }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [entries, profiles]);

  // Seed from API on mount + when mint changes.
  useEffect(() => {
    let cancelled = false;
    if (!mint) return;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/stats/recent-activity?mint=${encodeURIComponent(mint)}&limit=${MAX_ENTRIES}&min_usd=${MIN_USD}`
        );
        if (!r.ok) return;
        const { items } = await r.json();
        if (cancelled || !Array.isArray(items)) return;
        const seeded = items
          .map((it) => {
            if (it.kind === "launch") {
              return {
                id: it.id, kind: "launch", wallet: it.wallet, mint: it.mint,
                ts: +new Date(it.ts) || Date.now(),
              };
            }
            if (it.kind === "burn") {
              const decimals = Number(it.decimals) || 6;
              return {
                id: it.id, kind: "burn", mint: it.mint,
                amount_whole: Number(it.amount_raw) / 10 ** decimals,
                ts: +new Date(it.ts) || Date.now(),
              };
            }
            if (it.kind === "compound") {
              return {
                id: it.id, kind: "compound", mint: it.mint,
                usd: Number(it.added_usd) || 0,
                ts: +new Date(it.ts) || Date.now(),
              };
            }
            const sol = Number(it.sol_raw) / 1e9;
            const solUsd = Number(it.sol_usd);
            const usd = sol * solUsd;
            if (!Number.isFinite(usd)) return null;
            return {
              id: it.id, kind: it.kind, wallet: it.wallet, mint: it.mint, usd,
              ts: +new Date(it.ts) || Date.now(),
            };
          })
          .filter(Boolean);
        seeded.sort((a, b) => b.ts - a.ts);
        setEntries(seeded.slice(0, MAX_ENTRIES));
      } catch (err) {
        console.warn("[TokenLiveFeed] seed failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [mint]);

  // Subscribe to the firehose and filter by this mint.
  useEffect(() => {
    if (!slurpWs || !mint) return;
    const unsub = slurpWs.subscribe((msg) => {
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "trade:new" && msg.trade && msg.token?.mint === mint) {
        const sol = Number(msg.trade.sol_raw) / 1e9;
        const solUsd = Number(msg.trade.sol_usd);
        const usd = sol * solUsd;
        if (!Number.isFinite(usd) || usd < MIN_USD) return;
        const entry = {
          id: msg.trade.signature,
          kind: msg.trade.side === "buy" ? "buy" : "sell",
          wallet: msg.trade.trader,
          mint,
          usd,
          ts: Date.now(),
        };
        setEntries((prev) =>
          prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev].slice(0, MAX_ENTRIES)
        );
        return;
      }
      if (msg.type === "burn:executed" && msg.burn?.mint === mint) {
        const b = msg.burn;
        const decimals = Number(b.decimals) || 6;
        const entry = {
          id: `burn:${b.signature}`, kind: "burn", mint,
          amount_whole: Number(b.amount_raw) / 10 ** decimals,
          ts: Date.now(),
        };
        setEntries((prev) =>
          prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev].slice(0, MAX_ENTRIES)
        );
        return;
      }
      if (msg.type === "compound:executed" && msg.compound?.mint === mint) {
        const c = msg.compound;
        const entry = {
          id: `compound:${c.signature}`, kind: "compound", mint,
          usd: Number(c.added_usd) || 0,
          ts: Date.now(),
        };
        setEntries((prev) =>
          prev.some((e) => e.id === entry.id) ? prev : [entry, ...prev].slice(0, MAX_ENTRIES)
        );
      }
    });
    return unsub;
  }, [mint]);

  const filters = useMemo(() => buildFilters(feeMode), [feeMode]);
  const visible = useMemo(() => {
    if (filter === "all") return entries;
    return entries.filter((e) => e.kind === filter);
  }, [entries, filter]);

  return (
    <aside className="pm-tlf" aria-label="Live activity for this token">
      <div className="pm-tlf__head">
        <span className="pm-tlf__pulse" aria-hidden="true" />
        <span className="pm-tlf__label">
          LIVE {symbol ? `· $${symbol.toUpperCase()}` : ""}
        </span>
      </div>
      <div className="pm-tlf__filters" role="tablist">
        {filters.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={filter === key}
            className={`pm-tlf__filter${filter === key ? " is-active" : ""}`}
            onClick={() => setFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {visible.length === 0 ? (
        <div className="pm-tlf__empty">
          {entries.length === 0
            ? "Waiting for the next move…"
            : `No ${filter} events yet.`}
        </div>
      ) : (
        <ul className="pm-tlf__list">
          {visible.map((e) => {
            // Profile-aware display: prefer username if set, else last
            // 4 wallet chars. profiles[wallet] === null means we looked
            // up and confirmed no profile exists.
            const p = e.wallet ? profiles[e.wallet] : null;
            const who = p?.username ? `@${p.username}` : shortAddr(e.wallet);
            return (
              <li key={e.id} className={`pm-tlf__row pm-tlf__row--${e.kind}`}>
                <div className="pm-tlf__body">
                  <div className="pm-tlf__line">
                    {e.kind === "buy" && (
                      <>
                        <span className="pm-tlf__verb pm-tlf__verb--buy">BUY</span>
                        <span className="pm-tlf__amt">{fmtUsd(e.usd)}</span>
                        <span className="pm-tlf__by">by {who}</span>
                      </>
                    )}
                    {e.kind === "sell" && (
                      <>
                        <span className="pm-tlf__verb pm-tlf__verb--sell">SELL</span>
                        <span className="pm-tlf__amt">{fmtUsd(e.usd)}</span>
                        <span className="pm-tlf__by">by {who}</span>
                      </>
                    )}
                    {e.kind === "burn" && (
                      <>
                        <span className="pm-tlf__verb pm-tlf__verb--burn">BURN</span>
                        <span className="pm-tlf__amt">{fmtWhole(e.amount_whole)} burned</span>
                      </>
                    )}
                    {e.kind === "compound" && (
                      <>
                        <span className="pm-tlf__verb pm-tlf__verb--compound">+LP</span>
                        <span className="pm-tlf__amt">{fmtUsd(e.usd)} added</span>
                      </>
                    )}
                    {e.kind === "launch" && (
                      <>
                        <span className="pm-tlf__verb pm-tlf__verb--launch">LAUNCH</span>
                        <span className="pm-tlf__by">by {who}</span>
                      </>
                    )}
                  </div>
                  <span className="pm-tlf__time">{fmtAgo(e.ts)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
